import React, { useEffect, useState } from 'react';
import { UseFormMethods } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import CardContainer from 'src/components/common/CardContainer.redesign';
import Button from 'src/components/controls/forms/redesign/FormButton';
import { LandSystem } from 'src/models/LandSystem';
import OwnershipContactCategory from 'src/models/OwnershipContactCategory';
import Ownership from 'src/models/tract_detail/Ownership';
import { DialogContext, dialogCreators } from 'src/providers/DialogProvider';
import { IRootState } from 'src/reducers';
import MineralTractCard from './MineralTractCard';
import County from 'src/models/County';
import TractDetail from 'src/models/tract_detail/TractDetail';
import Dialog from 'src/components/common/Dialog';
import HorizonIntervalComponent from '../../TractComponents/AddHorizonComponents/HorizonIntervalComponent';
import DeleteConfirmDialog from 'src/components/admin/DeleteConfirmDialog';
import HorizonSequencingComponent from '../../TractComponents/AddHorizonComponents/HorizonSequencingComponent';
import DialogList from 'src/dialogs';
import { Formation, HorizonInterval } from 'src/models/Horizon';
import { useFetch } from 'use-http';
import Horizon from 'src/models/tract_detail/Horizon';
import actions from 'src/actions';
import WrappedForm from 'src/components/controls/forms/Form';
import FormTextBox from 'src/components/controls/forms/redesign/FormTextbox';
import _ from 'lodash';
import CreateHorizonDialog from '../dialogs/CreateHorizonDialog';
import { IHorizonSectionProps } from './HorizonSection';
import { SelectItem } from 'src/models/common/SelectItem';
import { OwnershipCategoryType } from 'src/models/OwnershipCategory';

interface IMineralTractListProps extends Partial<Pick<UseFormMethods, 'register' | 'errors' | 'getValues' | 'formState' | 'reset'>> {
    landSystem: LandSystem;
    ownershipContactCategories: Array<OwnershipContactCategory>;
    categoryTypes: Array<OwnershipCategoryType>;
    mineralTracts: Array<TractDetail>;
    counties: Array<County>;
    leaseBuyerList: Array<SelectItem>;
    loadSurfaceTract: () => void;
    openContactNotes: (ownership: Ownership) => void;
}

const MineralTractList = (props: IMineralTractListProps) => {
    const appContext = useSelector((state: IRootState) => state.appContext);
    const dialogContext = React.useContext<dialogCreators>(DialogContext);
    const { get, post, put, del, response } = useFetch();
    const dispatch = useDispatch();

    const [loading, setLoading] = useState<boolean>(true);
    // Horizon Dialog properties
    const [addFormationShowing, setAddFormationShowing] = useState<boolean>(false);
    const [deleteHorizonText, setDeleteHorizonText] = useState<string>('');
    const [errorMessage, setErrorMessage] = useState<string>('');
    const [formations, setFormations] = useState<Array<Formation>>([]);
    const [selectedHorizonNumber, setSelectedHorizonNumber] = useState<number>(undefined);
    const [horizonData, setHorizonData] = useState<Horizon>(undefined);
    const [horizonIntervals, setHorizonIntervals] = useState<Array<HorizonInterval>>([]);
    const [selectedTract, setSelectedTract] = useState<number>(undefined);
    // For Create horizon dialog
    const [horizonSectionProps, setHorizonSectionProps] = useState<IHorizonSectionProps>(undefined);

    useEffect(() => {
        if (props.mineralTracts) {
            setLoading(false);
            getFormations();
        }
    }, [props.mineralTracts]);

    useEffect(() => {
        if (props.mineralTracts?.[selectedTract]?.horizons?.length > 0) {
            setHorizonIntervals(
                props.mineralTracts?.[selectedTract]?.horizons
                    .map((hor: Horizon) => HorizonInterval.from(hor.horizonInterval))
                    .sort((a: HorizonInterval, b: HorizonInterval) => a.sequencePosition - b.sequencePosition)
            );
        }

        setHorizonData(props.mineralTracts?.[selectedTract]?.horizons?.filter((h) => h.horizonInterval.horizonNumber === selectedHorizonNumber)[0]);
    }, [selectedTract]);

    const getFormations = async () => {
        const result = await get(`v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/formations`);

        if (response.ok) {
            // CHECK THIS
            setFormations(result);
        }
    };

    const createMineralTract = () => {
        dialogContext.tract.createTract(props.landSystem, appContext.sectionNumber, true, props.counties, 'Mineral', null, async () => {
            props.loadSurfaceTract();
        });
    };

    const containerHeader = () => {
        return (
            <>
                <fieldset style={{ padding: '.3rem 0', borderWidth: '1px 0 0 0' }}>
                    <legend className="legend">Mineral Tract Information</legend>
                </fieldset>
                <div style={{ textAlign: 'right', marginRight: '1rem' }}>
                    <Button
                        text={`Add Mineral ${props.landSystem === LandSystem.Eastern ? 'Parcel' : 'Tract'}`}
                        size="medium"
                        intent="primary"
                        onClick={() => createMineralTract()}
                        disabled={props.formState?.isDirty}
                    />
                </div>
            </>
        );
    };

    const renderMineralCards = () => {
        return props.mineralTracts.map((mt: TractDetail, index: number) => {
            mt.isChild = true;
            return (
                <div key={index}>
                    <MineralTractCard
                        index={index}
                        tractDetail={mt}
                        landSystem={props.landSystem}
                        mineralTractAcreage={mt.grossAcres}
                        contactCategories={props.ownershipContactCategories}
                        categoryTypes={props.categoryTypes}
                        leaseBuyerList={props.leaseBuyerList}
                        horizons={mt.horizons}
                        openContactNotes={function (): void {
                            throw new Error('Function not implemented.');
                        }}
                        formations={formations}
                        loadSurfaceTract={() => props.loadSurfaceTract()}
                        register={props.register}
                        errors={props.errors}
                        getValues={props.getValues}
                        formState={props.formState}
                        reset={props.reset}
                        onAddFormation={onAddFormation}
                        sequenceHorizons={sequenceHorizons}
                        updateCurrentTract={updateSelectedTract}
                        showDialog={showNewDialog}
                        onOpenHorizonDialog={(dialogParams: IHorizonSectionProps) => onOpenHorizonDialog(dialogParams)}
                    />
                </div>
            );
        });
    };

    const onDeleteHorizon = async (horizonNumber: string) => {
        if (
            horizonNumber === deleteHorizonText ||
            deleteHorizonText === `${appContext.sectionNumber}.${props.mineralTracts?.[selectedTract]?.tractNumber}-${horizonNumber}`
        ) {
            const result = await del(
                `v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/fields/${appContext.sectionNumber}/tracts/
                 ${props.mineralTracts?.[selectedTract]?.tractNumber}/horizons/${horizonNumber}`
            );

            if (response.ok) {
                setSelectedHorizonNumber(0);
                props.loadSurfaceTract();
                dispatch(actions.layout.hideDialog());
            } else {
                setErrorMessage(result.message);
            }
        }
    };

    const updateDeleteHorizonText = (text: string) => {
        setDeleteHorizonText(text);
    };

    const addFormationDialog = () => {
        return (
            <WrappedForm onSubmit={onAddFormation}>
                <FormTextBox name="name" label="Formation Name" />
                <Button type="submit" text="Add Formation" intent="primary" />
                <Button buttonStyle="link" text="Cancel" onClick={() => setAddFormationShowing(false)} />
            </WrappedForm>
        );
    };

    const onAddFormation = async (formation: Formation) => {
        console.log('onaddFormation');

        await post(`v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/formations`, formation);

        if (response.ok) {
            props.loadSurfaceTract();
            setAddFormationShowing(false);
        }
    };

    const onUpdateHorizonInterval = (horizonInterval: HorizonInterval) => {
        if (horizonInterval.from.type === 'formation') {
            horizonInterval.from.custom = undefined;
            horizonInterval.from.depth = undefined;
        } else if (horizonInterval.from.type === 'depth') {
            horizonInterval.from.custom = undefined;
            horizonInterval.from.namedHorizon = undefined;
        } else if (horizonInterval.from.type === 'custom') {
            horizonInterval.from.depth = undefined;
            horizonInterval.from.namedHorizon = undefined;
        } else {
            horizonInterval.from.depth = undefined;
            horizonInterval.from.custom = undefined;
            horizonInterval.from.namedHorizon = undefined;
        }

        if (horizonInterval.to.type === 'formation') {
            horizonInterval.to.custom = undefined;
            horizonInterval.to.depth = undefined;
        } else if (horizonInterval.to.type === 'depth') {
            horizonInterval.to.custom = undefined;
            horizonInterval.to.namedHorizon = undefined;
        } else if (horizonInterval.to.type === 'custom') {
            horizonInterval.to.depth = undefined;
            horizonInterval.to.namedHorizon = undefined;
        } else {
            horizonInterval.to.depth = undefined;
            horizonInterval.to.custom = undefined;
            horizonInterval.to.namedHorizon = undefined;
        }

        horizonInterval.from.type = undefined;
        horizonInterval.to.type = undefined;

        let horizonIndex = props.mineralTracts?.[selectedTract]?.horizons.indexOf(
            props.mineralTracts?.[selectedTract]?.horizons.find((h) => h.horizonInterval.horizonNumber === selectedHorizonNumber)
        );

        if (horizonIndex > -1) {
            horizonInterval.id = props.mineralTracts?.[selectedTract]?.horizons[horizonIndex].horizonInterval.id;
            updateHorizonInterval(
                props.mineralTracts?.[selectedTract]?.tractNumber,
                props.mineralTracts?.[selectedTract]?.horizons[selectedTract].horizonInterval.horizonNumber,
                horizonInterval
            );
        }
    };

    const onOpenHorizonDialog = (dialogParams: IHorizonSectionProps) => {
        setHorizonSectionProps(dialogParams);
        dispatch(actions.layout.showDialog(DialogList.ADD_HORIZON));
    };

    const updateHorizonInterval = async (tractNumber: number, horizonNumber: number, interval: HorizonInterval) => {
        await put(
            `v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/fields/${appContext.sectionNumber}/tracts/${tractNumber}/horizons/${horizonNumber}/horizon-interval`,
            interval
        );

        if (response.ok) {
            props.loadSurfaceTract();
            dispatch(actions.layout.hideDialog());
        }
    };

    const sequenceHorizons = async (sequence: Array<{ id: number; sequence: number }>) => {
        console.log('sequenceHorizons');

        await post(
            `v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/fields/${appContext.sectionNumber}/tracts/${props.mineralTracts?.[selectedTract]?.tractNumber}/sequence-horizons`,
            sequence
        );

        if (response.ok) {
            props.loadSurfaceTract();
            dispatch(actions.layout.hideDialog());
        }
    };

    const updateSelectedTract = (tractId: number, horizonNumber: number) => {
        console.log('updateSelectedTract');

        let tract = props.mineralTracts?.indexOf(props.mineralTracts.find((mt) => mt.id === tractId));
        setSelectedTract(tract);
        setSelectedHorizonNumber(horizonNumber);
    };

    const showNewDialog = (dialogName: string) => {
        setTimeout(() => dispatch(actions.layout.showDialog(dialogName)), 250);
    };

    return (
        <>
            {props.mineralTracts?.length > 0 && (
                <>
                    {containerHeader()}
                    <CardContainer>{renderMineralCards()}</CardContainer>
                </>
            )}
            {props.mineralTracts?.length === 0 && (
                <>
                    {containerHeader()}
                    <div
                        style={{
                            margin: '1rem',
                            padding: '1rem',
                            height: '4rem',
                            fontWeight: 'bold',
                            border: '2px solid red',
                            fontSize: '1.1em',
                            textAlign: 'center',
                        }}
                    >
                        No Mineral Tracts found
                    </div>
                </>
            )}
            {loading && (
                <>
                    {containerHeader()}
                    <div
                        style={{
                            margin: '1rem',
                            padding: '1rem',
                            height: '4rem',
                            fontWeight: 'bold',
                            border: '2px solid #ccc',
                            fontSize: '1.1em',
                            textAlign: 'center',
                        }}
                    >
                        Loading ...
                    </div>
                </>
            )}

            <Dialog
                title="Edit Horizon"
                dialogName={DialogList.EDIT_HORIZON}
                subDialogHeader="Add Formation"
                subDialogShowing={addFormationShowing}
                subDialog={addFormationDialog}
            >
                <HorizonIntervalComponent
                    formations={formations}
                    showAddFormationPanel={() => setAddFormationShowing(true)}
                    horizonInterval={horizonData?.horizonInterval}
                    updateHorizonInterval={onUpdateHorizonInterval}
                    onNext={(horizonInterval: HorizonInterval) => onUpdateHorizonInterval(horizonInterval)}
                    onPrev={() => undefined}
                />
            </Dialog>

            <DeleteConfirmDialog
                title="Delete Horizon"
                promptText="Type Horizon number to confirm delete"
                dialogName={DialogList.DELETE_HORIZON}
                delete={onDeleteHorizon}
                item={selectedHorizonNumber ? selectedHorizonNumber.toString() : ''}
                updateText={updateDeleteHorizonText}
                errorMessage={errorMessage}
                onCloseDialog={() => dispatch(actions.tract.clearError())}
            />

            <Dialog title="Sequence Horizons" dialogName={DialogList.SEQUENCE_HORIZONS}>
                <HorizonSequencingComponent horizons={horizonIntervals} sequenceHorizons={sequenceHorizons} customDetailFormat={(item) => <div>{item.toString()}</div>}/>
            </Dialog>

            <CreateHorizonDialog {...horizonSectionProps} onCloseDialog={() => dispatch(actions.layout.hideDialog())} />
        </>
    );
};

export default MineralTractList;



// WEBPACK FOOTER //
// ./src/components/project/sections/tract/components/MineralTractList.tsx